"use client";
import InfoIcon from "@mui/icons-material/Info";
import { Tooltip, tooltipClasses, TooltipProps } from "@mui/material";
import { styled } from "@mui/material/styles";
import { createContext, useContext, useEffect, useState } from "react";

/**
 * Context for providing a custom container element for tooltips.
 * Useful when rendering tooltips inside iframes where the default
 * document.body would be the parent window's body.
 */
export const TooltipContainerContext = createContext<HTMLElement | null>(null);

interface ITooltipProps extends Omit<TooltipProps, "children"> {
    children: React.ReactNode;
}
export const HTooltip: React.FC<ITooltipProps> = ({ children, enterTouchDelay, ...tooltipProps }) => {
    const [isTooltipOpen, setIsTooltipOpen] = useState(false);

    useEffect(() => {
        window.addEventListener("scroll", () => setIsTooltipOpen(false));
        return () => window.removeEventListener("scroll", () => setIsTooltipOpen(false));
    }, []);

    return (
        <Tooltip
            open={isTooltipOpen}
            onMouseEnter={() => setIsTooltipOpen(true)}
            onMouseLeave={() => setIsTooltipOpen(false)}
            {...tooltipProps}
            enterTouchDelay={enterTouchDelay || 300}
        >
            <div>{children}</div>
        </Tooltip>
    );
};

const FollowTooltipInner = ({ className, ...props }: ITooltipProps & { className?: string }) => {
    const container = useContext(TooltipContainerContext);
    return (
        <HTooltip
            followCursor
            placement="right-start"
            {...props}
            classes={{ popper: className }}
            PopperProps={container ? { container } : undefined}
        />
    );
};

export const FollowTooltip = styled(FollowTooltipInner)({
    [`& .${tooltipClasses.tooltip}`]: {
        marginTop: "16px",
        padding: "8px",
        fontFamily: "Inter-Medium",
        fontSize: "12px",
        translateX: "0px",
    },
});

export const InfoTooltip = ({
    title,
}: {
    /** Text to be inserted inside of the tooltip */
    title: React.ReactNode;
}) => (
    <FollowTooltip title={title}>
        <InfoIcon />
    </FollowTooltip>
);

export const InfoTooltipWrapper = ({
    tooltipText,
    children,
    iconRight,
    hideTooltip,
}: {
    /** Text to be inserted inside of the tooltip */
    tooltipText: string;
    /** Elements to be displayed to the left of the icon */
    children: React.ReactNode;
    /** Applies justify-content: space-between, sending the icon the right most it can go */
    iconRight?: boolean;
    /** Doesn't add tooltip, useful for exports */
    hideTooltip?: boolean;
}) => {
    if (hideTooltip) return <>{children}</>;
    return (
        <div style={{ display: "flex", justifyContent: iconRight ? "space-between" : "normal" }}>
            <div style={{ marginRight: "0.5rem" }}>{children}</div>
            <InfoTooltip title={tooltipText} />
        </div>
    );
};
